import type { QueryClient } from '@tanstack/react-query'
import type { InventoryProduct } from './inventoryTypes'

export type InventoryListQueryData = {
  products: InventoryProduct[]
  hasMore: boolean
  total: number
  exactMatch?: InventoryProduct | null
}

export function snapshotInventoryListQueries(queryClient: QueryClient) {
  return queryClient.getQueriesData({ queryKey: ['inventory', 'list'] }) as Array<
    [readonly unknown[], InventoryListQueryData | undefined]
  >
}

export function restoreInventoryListQueries(
  queryClient: QueryClient,
  snapshot: ReturnType<typeof snapshotInventoryListQueries>,
) {
  snapshot.forEach(([key, data]) => {
    queryClient.setQueryData(key, data)
  })
}

export function optimisticRemoveProducts(queryClient: QueryClient, removedIds: Set<string>) {
  queryClient.setQueriesData({ queryKey: ['inventory', 'list'] }, (old: InventoryListQueryData | undefined) => {
    if (!old?.products) return old
    const products = old.products.filter((p) => !removedIds.has(p.id))
    const removedCount = old.products.length - products.length
    return {
      ...old,
      products,
      total: Math.max(0, old.total - removedCount),
    }
  })
}

export function optimisticClearSales(queryClient: QueryClient, ids: Set<string>) {
  queryClient.setQueriesData({ queryKey: ['inventory', 'list'] }, (old: InventoryListQueryData | undefined) => {
    if (!old?.products) return old
    const products = old.products.map((p) => {
      if (!ids.has(p.id)) return p
      return {
        ...p,
        onSale: false,
        saleType: undefined,
        saleValue: undefined,
        saleStartAt: null,
        saleEndAt: null,
        saleInfo: undefined,
      }
    })
    return { ...old, products }
  })
}

/**
 * Put a batch sale's result on the cached rows: the sale fields AND the listed price the server
 * re-priced them at. A sale changes the listed price, which only the engine can work out (rate,
 * floor, markup, typed amounts), so the rows are not touched before the server answers — the old
 * optimistic patch set the sale flags at once and left the OLD listed price beside them until
 * the refetch landed, and showed a sale on rows the server then refused.
 */
export function applyRepricedSaleRows(
  queryClient: QueryClient,
  rows: ReadonlyArray<Pick<InventoryProduct, 'id'> & Partial<InventoryProduct>>,
) {
  if (rows.length === 0) return
  const byId = new Map(rows.map((row) => [row.id, row]))
  const patch = (p: InventoryProduct): InventoryProduct => {
    const row = byId.get(p.id)
    return row ? { ...p, ...row } : p
  }
  queryClient.setQueriesData({ queryKey: ['inventory', 'list'] }, (old: InventoryListQueryData | undefined) => {
    if (!old?.products) return old
    return { ...old, products: old.products.map(patch), exactMatch: old.exactMatch ? patch(old.exactMatch) : old.exactMatch }
  })
}

export function optimisticClearManualPrice(queryClient: QueryClient, productId: string) {
  queryClient.setQueriesData({ queryKey: ['inventory', 'list'] }, (old: InventoryListQueryData | undefined) => {
    if (!old?.products) return old
    const products = old.products.map((p) => {
      if (p.id !== productId) return p
      return {
        ...p,
        manualPriceCents: null,
        manualPriceExpiresAt: null,
      }
    })
    return { ...old, products }
  })
}
